import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';
import clientCandidateDataService from '../../../services/clientCandidateDataService';

const API = API_BASE_URL;

/**
 * PositionsWidget
 * Employment history with:
 * - Location (optional) for each position
 * - Multi-step positions (multiple titles at same company over time)
 * - Grouped by company with sub-positions
 * - Company names linked to /firm/:id
 */

const PositionsWidget = ({ profile, candidateId, onRefresh, refreshTrigger }) => {
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [allFirms, setAllFirms] = useState([]);
  const [firmDropdownOpen, setFirmDropdownOpen] = useState(false);
  const firmRef = useRef(null);
  const [formData, setFormData] = useState({
    company_name: '',
    job_title: '',
    location: '',
    start_date: '',
    end_date: '',
    is_current: false,
    description: '',
    firm_id: null
  });

  useEffect(() => {
    loadPositions();
    loadFirms();
    const handleClickOutside = (e) => {
      if (firmRef.current && !firmRef.current.contains(e.target)) setFirmDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [candidateId, refreshTrigger]);

  const loadFirms = async () => {
    try {
      const res = await fetch(`${API}/api/firms`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (res.ok) setAllFirms(await res.json());
    } catch (e) { /* firms list optional */ }
  };

  const loadPositions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/candidates/${candidateId}/employment`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Sort by company, then by start_date descending
        const sorted = data.sort((a, b) => {
          if (a.is_current && !b.is_current) return -1;
          if (!a.is_current && b.is_current) return 1;
          return new Date(b.start_date) - new Date(a.start_date);
        });
        setPositions(sorted);
        
        // Auto-expand all companies initially
        const expanded = {};
        sorted.forEach(p => { expanded[p.company_name] = true; });
        setExpandedCompanies(expanded);
      } else {
        throw new Error('Failed to load positions');
      }
    } catch (err) {
      console.error('Error loading positions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      job_title: '',
      location: '',
      start_date: '',
      end_date: '',
      is_current: false,
      description: '',
      firm_id: null
    });
    setFirmDropdownOpen(false);
  };

  const handleAdd = (prefillCompany = '', prefillFirmId = null) => {
    setFormData({
      company_name: prefillCompany,
      job_title: '',
      location: '',
      start_date: '',
      end_date: '',
      is_current: false,
      description: '',
      firm_id: prefillFirmId
    });
    setEditingId(null);
    setIsAdding(true);
  };

  const handleEdit = (position) => {
    setFormData({
      company_name: position.company_name || '',
      job_title: position.job_title || '',
      location: position.location || '',
      start_date: position.start_date ? position.start_date.substring(0, 7) : '',
      end_date: position.end_date ? position.end_date.substring(0, 7) : '',
      is_current: position.is_current || false,
      description: position.description || '',
      firm_id: position.firm_id || position.linked_firm_id || null
    });
    setEditingId(position.id);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    resetForm();
  };

  const handleSave = async () => {
    if (!formData.company_name || !formData.job_title || !formData.start_date) {
      alert('Please fill in Company, Job Title, and Start Date');
      return;
    }

    try {
      const payload = {
        company_name: formData.company_name,
        job_title: formData.job_title,
        location: formData.location || null,
        start_date: formData.start_date + '-01',
        end_date: formData.is_current ? null : (formData.end_date ? formData.end_date + '-01' : null),
        is_current: formData.is_current,
        description: formData.description || null,
        firm_id: formData.firm_id || null
      };

      const url = editingId 
        ? `${API}/api/candidates/${candidateId}/employment/${editingId}`
        : `${API}/api/candidates/${candidateId}/employment`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // Mark candidate as unclean since data was modified
        try {
          await clientCandidateDataService.markAsUnclean(candidateId);
        } catch (e) {
          console.log('Could not mark as unclean:', e);
        }
        handleCancel();
        loadPositions();
        if (onRefresh) onRefresh();
      } else {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving position:', err);
      alert('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async (positionId) => {
    if (!window.confirm('Delete this position?')) return;

    try {
      const response = await fetch(`${API}/api/candidates/${candidateId}/employment/${positionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });

      if (response.ok) {
        // Mark candidate as unclean since data was modified
        try {
          await clientCandidateDataService.markAsUnclean(candidateId);
        } catch (e) {
          console.log('Could not mark as unclean:', e);
        }
        loadPositions();
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error('Error deleting position:', err);
      alert('Failed to delete position');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    let months = (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth();
    if (end.getDate() < start.getDate()) months--;
    if (months < 0) months = 0;
    
    const years = Math.floor(months / 12);
    const rem = months % 12;
    
    if (years === 0) return rem === 1 ? '1 mo' : `${rem} mo`;
    if (rem === 0) return years === 1 ? '1 yr' : `${years} yrs`;
    return `${years === 1 ? '1 yr' : `${years} yrs`}, ${rem === 1 ? '1 mo' : `${rem} mo`}`;
  };

  // Group positions by company
  const groupByCompany = () => {
    const groups = {};
    const firmIds = {};
    // Track the company logo per group. We capture from the first position
    // in the company whose linked_firm_logo_url is set — meaning at least
    // one of the candidate's positions was matched to a firm record with a
    // logo on file. Companies that haven't been matched to a firm (or whose
    // firm has no logo yet) fall back to the colored status dot.
    const firmLogos = {};
    positions.forEach(pos => {
      if (!groups[pos.company_name]) {
        groups[pos.company_name] = [];
      }
      groups[pos.company_name].push(pos);
      // Track firm_id for this company (use first non-null)
      if (!firmIds[pos.company_name] && (pos.linked_firm_id || pos.firm_id)) {
        firmIds[pos.company_name] = pos.linked_firm_id || pos.firm_id;
      }
      if (!firmLogos[pos.company_name] && pos.linked_firm_logo_url) {
        firmLogos[pos.company_name] = pos.linked_firm_logo_url;
      }
    });
    
    Object.keys(groups).forEach(company => {
      groups[company].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
    });
    
    const sortedCompanies = Object.keys(groups).sort((a, b) => {
      const aHasCurrent = groups[a].some(p => p.is_current);
      const bHasCurrent = groups[b].some(p => p.is_current);
      if (aHasCurrent && !bHasCurrent) return -1;
      if (!aHasCurrent && bHasCurrent) return 1;
      return new Date(groups[b][0].start_date) - new Date(groups[a][0].start_date);
    });
    
    return { groups, sortedCompanies, firmIds, firmLogos };
  };

  const calculateCompanyDuration = (companyPositions) => {
    const earliest = companyPositions.reduce((min, p) => 
      new Date(p.start_date) < new Date(min.start_date) ? p : min
    );
    const hasCurrent = companyPositions.some(p => p.is_current);
    const latest = hasCurrent ? null : companyPositions.reduce((max, p) => {
      const endDate = p.end_date || p.start_date;
      return new Date(endDate) > new Date(max.end_date || max.start_date) ? p : max;
    });
    
    return calculateDuration(earliest.start_date, hasCurrent ? null : latest.end_date);
  };

  const toggleCompany = (company) => {
    setExpandedCompanies(prev => ({ ...prev, [company]: !prev[company] }));
  };

  const renderForm = () => (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-sm text-emerald-900">
          {editingId ? 'Edit Position' : 'Add New Position'}
        </h4>
        <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600" title="Cancel">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Company *</label>
            <div className="relative" ref={firmRef}>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => { setFormData({ ...formData, company_name: e.target.value, firm_id: null }); setFirmDropdownOpen(true); }}
                onFocus={() => setFirmDropdownOpen(true)}
                className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500"
                placeholder="Search firms or type new..."
              />
              {firmDropdownOpen && formData.company_name && (() => {
                const q = formData.company_name.toLowerCase().trim();
                const filtered = allFirms.filter(f => f.name.toLowerCase().includes(q)).slice(0, 6);
                if (filtered.length === 0) return null;
                return (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {filtered.map(f => (
                      <button key={f.id} type="button"
                        onClick={() => { setFormData({ ...formData, company_name: f.name, firm_id: f.id }); setFirmDropdownOpen(false); }}
                        className="w-full px-3 py-1.5 text-left text-sm hover:bg-emerald-50 flex items-center gap-2">
                        <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                        <span className="truncate">{f.name}</span>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Job Title *</label>
            <input
              type="text"
              value={formData.job_title}
              onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500"
              placeholder="Job title"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Location (optional)</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500"
            placeholder="e.g., New York, NY"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Start Date *</label>
            <input
              type="month"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="month"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              disabled={formData.is_current}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-100"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_current"
            checked={formData.is_current}
            onChange={(e) => setFormData({ ...formData, is_current: e.target.checked, end_date: '' })}
            className="rounded border-gray-300 text-emerald-700"
          />
          <label htmlFor="is_current" className="text-sm text-gray-700">Current position</label>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={2}
            className="w-full px-3 py-1.5 text-sm border rounded focus:ring-1 focus:ring-emerald-500"
            placeholder="Brief description of role..."
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={handleSave} className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700">
            {editingId ? 'Update' : 'Add Position'}
          </button>
          <button onClick={handleCancel} className="px-3 py-1.5 border text-sm rounded hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        <p className="text-sm">{error}</p>
        <button onClick={loadPositions} className="mt-2 text-xs text-emerald-700 hover:underline">Retry</button>
      </div>
    );
  }

  const { groups, sortedCompanies, firmIds, firmLogos } = groupByCompany();

  return (
    <div>
      {/* Add button */}
      {!isAdding && !editingId && (
        <button
          onClick={() => handleAdd()}
          className="w-full mb-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors flex items-center justify-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Position
        </button>
      )}

      {/* Form */}
      {(isAdding || editingId) && renderForm()}

      {/* Positions grouped by company */}
      {positions.length === 0 && !isAdding ? (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className="text-sm">No employment history</p>
          <p className="text-xs text-gray-400 mt-1">Click "Add Position" to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedCompanies.map((company) => {
            const companyPositions = groups[company];
            const isExpanded = expandedCompanies[company];
            const hasCurrent = companyPositions.some(p => p.is_current);
            const totalDuration = calculateCompanyDuration(companyPositions);

            return (
              <div key={company} className="border rounded-lg overflow-hidden">
                {/* Company Header */}
                <button
                  onClick={() => toggleCompany(company)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {/* Company logo with fallback. When the linked firm has
                        a logo_url, show it as a square thumbnail — gives the
                        positions section a much more "real company" feel
                        than abstract colored dots. The status dot (green
                        for current, gray otherwise) overlays the logo's
                        bottom-right as a small badge so the "is this their
                        current employer" signal is still glance-able. */}
                    {firmLogos[company] ? (
                      <div className="relative w-7 h-7 flex-shrink-0">
                        <img
                          src={firmLogos[company]}
                          alt={`${company} logo`}
                          className="w-7 h-7 rounded object-contain border border-gray-200 bg-white"
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-gray-50 ${hasCurrent ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                      </div>
                    ) : (
                      <div className={`w-3 h-3 rounded-full ${hasCurrent ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    )}
                    {firmIds[company] ? (
                      <span onClick={(e) => { e.stopPropagation(); navigate(`/firm/${firmIds[company]}`); }}
                        className="font-semibold text-emerald-700 text-sm hover:text-emerald-800 hover:underline cursor-pointer flex items-center gap-1">
                        {company}
                        <svg className="w-3 h-3 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                      </span>
                    ) : (
                      <span className="font-semibold text-gray-900 text-sm">{company}</span>
                    )}
                    <span className="text-xs text-gray-500">
                      {companyPositions.length} position{companyPositions.length > 1 ? 's' : ''} · {totalDuration}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAdd(company, firmIds[company]); }}
                      className="p-1 text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 rounded"
                      title="Add another position at this company"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* Positions within company */}
                {isExpanded && (
                  <div className="divide-y divide-gray-100">
                    {companyPositions.map((position, index) => (
                      <div key={position.id} className="px-3 py-2 group relative">
                        <div className="flex items-start gap-3">
                          {/* Timeline indicator */}
                          <div className="flex flex-col items-center mt-1">
                            <div className={`w-2 h-2 rounded-full ${position.is_current ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            {index < companyPositions.length - 1 && (
                              <div className="w-0.5 h-full bg-gray-200 mt-1"></div>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-sm font-medium text-gray-800">{position.job_title}</p>
                                <p className="text-xs text-gray-500">
                                  {formatDate(position.start_date)} – {position.is_current ? (
                                    <span className="text-green-600 font-medium">Present</span>
                                  ) : formatDate(position.end_date)}
                                  <span className="text-gray-400 ml-1">
                                    · {calculateDuration(position.start_date, position.is_current ? null : position.end_date)}
                                  </span>
                                </p>
                                {position.location && (
                                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    </svg>
                                    {position.location}
                                  </p>
                                )}
                                {position.description && (
                                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">{position.description}</p>
                                )}
                              </div>

                              {/* Edit/Delete buttons */}
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                <button
                                  onClick={() => handleEdit(position)}
                                  className="p-1 text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 rounded"
                                  title="Edit"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDelete(position.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  title="Delete"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PositionsWidget;
